/**
 * RenameInline — inline title editor for a sidebar thread (STORY-002-010 step 5).
 *
 * Renders an autofocused text input in place of the thread row. Enter confirms,
 * Escape cancels, blur confirms (or cancels if empty). Focus management (AC#10):
 * the input grabs focus on mount and the caret is selected so the user can type
 * over the old title immediately.
 *
 * Tokens come from the Tailwind theme; strings from the `chat` i18n namespace.
 */
import { useEffect, useRef, useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";

const MAX_TITLE_CHARS = 200;

export interface RenameInlineProps {
  initialTitle: string;
  onConfirm: (title: string) => void;
  onCancel: () => void;
}

export function RenameInline({
  initialTitle,
  onConfirm,
  onCancel,
}: RenameInlineProps): ReactElement {
  const { t } = useTranslation("chat");
  const [value, setValue] = useState(initialTitle);
  const inputRef = useRef<HTMLInputElement | null>(null);
  // Guard so a confirm-on-Enter doesn't also fire confirm-on-blur.
  const settled = useRef(false);

  useEffect(() => {
    const el = inputRef.current;
    if (el) {
      el.focus();
      el.select();
    }
  }, []);

  const confirm = (): void => {
    if (settled.current) return;
    const trimmed = value.trim();
    if (trimmed.length === 0) {
      settled.current = true;
      onCancel();
      return;
    }
    settled.current = true;
    onConfirm(trimmed.slice(0, MAX_TITLE_CHARS));
  };

  const cancel = (): void => {
    if (settled.current) return;
    settled.current = true;
    onCancel();
  };

  return (
    <input
      ref={inputRef}
      type="text"
      value={value}
      maxLength={MAX_TITLE_CHARS}
      aria-label={t("rename.label")}
      data-testid="chat-rename-input"
      onChange={(e) => setValue(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          confirm();
        } else if (e.key === "Escape") {
          e.preventDefault();
          cancel();
        }
      }}
      onBlur={confirm}
      className="min-h-[40px] w-full rounded-[10px] border border-[color:var(--g-primary)] bg-background px-2.5 py-2 text-[15px]/[1.429] text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring"
    />
  );
}

export default RenameInline;
